/**
 * AuthContext.tsx — App-level auth context for v4
 *
 * Boot: silent refresh (httpOnly cookie) BEFORE treating user as logged out.
 * Wrap <App /> with <AuthProvider> in main.tsx.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import * as Sentry from '@sentry/react'
import { apiFetch, getToken, setAccessToken, silentRefreshSession } from '../api/client'
import { identifyUser, resetPosthog } from '../lib/posthog'

export interface AuthUser {
  id:                      number
  name:                    string
  email:                   string
  email_verified:          boolean
  user_role:               'vendor' | 'contractor' | 'gc' | 'sub' | null
  portal_session?:         'vendor' | null
  company_name:            string | null
  company_role?:           'owner' | 'admin' | 'finance' | null
  is_admin?:               boolean
  subscription_status:     string
  plan_type:               string
  has_completed_onboarding: boolean
  trial_start_date:        string
  trial_end_date:          string
  created_at?:             string
  has_contractor_projects?: boolean
  has_vendor_portal?:      boolean
  needs_portal_choice?:    boolean
  redirect_to?:            string
}

export interface AuthContextValue {
  user:    AuthUser | null
  loading: boolean
  error:   string | null
  refetch: () => void
}

const AuthContext = createContext<AuthContextValue>({
  user:    null,
  loading: true,
  error:   null,
  refetch: () => {},
})

function applyIdentity(user: AuthUser | null) {
  if (user) {
    identifyUser(user)
    Sentry.setUser({ id: String(user.id), email: user.email })
  } else {
    resetPosthog()
    Sentry.setUser(null)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)

  const fetchUser = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await silentRefreshSession()

      try {
        const raw = await apiFetch<AuthUser & { token?: string }>('GET', '/api/auth/me')
        if (raw.token) setAccessToken(raw.token)
        const { token: _ignored, ...u } = raw
        setUser(u)
        applyIdentity(u)
        return
      } catch {
        if (getToken()) {
          await apiFetch('POST', '/api/auth/establish-session', { keepSignedIn: true })
          const raw = await apiFetch<AuthUser & { token?: string }>('GET', '/api/auth/me')
          if (raw.token) setAccessToken(raw.token)
          const { token: _ignored, ...u } = raw
          setUser(u)
          applyIdentity(u)
          return
        }
      }
      setUser(null)
      applyIdentity(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Auth failed')
      setUser(null)
      setAccessToken(null)
      applyIdentity(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // Legacy Google OAuth hash tokens (pre-cookie migration)
    const hash = window.location.hash
    if (hash.includes('google_token=')) {
      const token = new URLSearchParams(hash.slice(1)).get('google_token')
      if (token) {
        setAccessToken(token)
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
      }
    }
    fetchUser()
    const onLogout = () => {
      setUser(null)
      setLoading(false)
      applyIdentity(null)
    }
    window.addEventListener('auth:logout', onLogout)
    return () => window.removeEventListener('auth:logout', onLogout)
  }, [fetchUser])

  return (
    <AuthContext.Provider value={{ user, loading, error, refetch: fetchUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

export function isVendor(user: AuthUser | null): boolean {
  return user?.portal_session === 'vendor'
}

export function isContractor(user: AuthUser | null): boolean {
  if (!user) return false
  return user.portal_session !== 'vendor'
}
